import { useEffect, useState } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { permissionAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Shield, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const ROLES = ['owner', 'admin', 'manager', 'team'];
const PERM_LABELS = {
  view: 'View tasks & pages',
  create: 'Create tasks & pages',
  edit: 'Edit tasks & pages',
  assign: 'Assign tasks to others',
  comment: 'Add comments',
  change_status: 'Change task status',
  access_dashboards: 'Access dashboards & reports',
  manage_members: 'Manage team members',
  manage_settings: 'Manage workspace settings',
  delete: 'Delete tasks & pages',
};

export default function PermissionsPage() {
  const { currentWorkspace } = useWorkspace();
  const [perms, setPerms] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [activeRole, setActiveRole] = useState('manager');

  useEffect(() => {
    if (!currentWorkspace?.id) return;
    setLoading(true);
    permissionAPI.get(currentWorkspace.id)
      .then(res => setPerms(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [currentWorkspace?.id]);

  const togglePerm = async (role, perm) => {
    const current = perms[role]?.[perm] ?? false;
    const updated = { ...perms[role], [perm]: !current };
    setPerms(p => ({ ...p, [role]: updated }));
    setSaving(perm);
    try {
      await permissionAPI.update(currentWorkspace.id, role, updated);
    } catch { toast.error('Failed to save'); }
    finally { setSaving(null); }
  };

  if (loading) return <div className="flex items-center justify-center h-full"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="max-w-3xl mx-auto p-8 space-y-6" data-testid="permissions-page">
      <div>
        <h1 className="text-2xl font-display font-bold tracking-tight">Permissions</h1>
        <p className="text-sm text-muted-foreground mt-1">Customize what each role can do in the Task Manager.</p>
      </div>

      <Tabs value={activeRole} onValueChange={setActiveRole}>
        <TabsList>
          {ROLES.map(r => <TabsTrigger key={r} value={r} className="capitalize text-xs">{r}</TabsTrigger>)}
        </TabsList>

        {ROLES.map(role => (
          <TabsContent key={role} value={role} className="mt-4">
            <Card className="bg-card/60 backdrop-blur">
              <CardContent className="p-0">
                {Object.entries(PERM_LABELS).map(([key, label], i) => {
                  const enabled = perms[role]?.[key] ?? false;
                  const isLocked = role === 'owner'; // Owner always has full access
                  return (
                    <div key={key} className={`flex items-center justify-between px-5 py-3.5 ${i > 0 ? 'border-t border-border/30' : ''}`}>
                      <div>
                        <p className="text-[13px] font-medium">{label}</p>
                      </div>
                      <Switch
                        checked={isLocked ? true : enabled}
                        disabled={isLocked}
                        onCheckedChange={() => togglePerm(role, key)}
                        data-testid={`perm-${role}-${key}`}
                      />
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
